import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  TrendingUp,
  ShoppingBag,
  Receipt,
  Trophy,
  CalendarDays,
  Clock,
  RefreshCw,
  ChevronRight,
  PackageCheck,
  Download,
} from "lucide-react";
import api from "../api/api";
import { formatDateTime } from "../utils/datetime";

function RekapEvent() {
  const { eventId } = useParams();
  const navigate    = useNavigate();

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [exporting, setExporting] = useState(false);

  const fetchRecap = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/rekap/events/${eventId}`);
      setData(res.data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.message || "Gagal mengambil rekap event");
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { fetchRecap(); }, [fetchRecap]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const res = await api.get(`/rekap/events/${eventId}/export`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `dashboard-event-${eventId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (e) {
      console.error("Export error:", e);
    } finally {
      setExporting(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center p-12 text-sm text-zinc-500">
        <RefreshCw className="h-5 w-5 animate-spin mr-2" /> Memuat rekap global event...
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-medium text-rose-700">
        {error}
      </div>
    );
  }
  
  if (!data) return null;

  const { event, summary, sessions, top_products } = data;

  return (
    <div className="space-y-6 animate-fade-in pb-10 max-w-[1400px]">
      {/* Header */}
      <header className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/events/${eventId}`)}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-200 bg-white hover:bg-zinc-50 transition-colors focus:outline-none focus:ring-1 focus:ring-zinc-900"
            >
              <ArrowLeft className="h-4 w-4 text-zinc-600" />
            </button>
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-zinc-600 shadow-sm">
                <Receipt className="h-3 w-3 text-indigo-600" />
                Rekap Global Event
              </div>
              <h1 className="mt-2 text-xl font-semibold tracking-tight text-zinc-900 md:text-2xl">
                {event.name}
              </h1>
              <div className="mt-1 flex items-center gap-1.5 text-[11px] font-medium text-zinc-500">
                <CalendarDays className="h-3.5 w-3.5" />
                {event.start_date} — {event.end_date}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <button onClick={fetchRecap} disabled={loading} className="inline-flex items-center justify-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 text-[13px] font-medium text-zinc-700 shadow-sm hover:bg-zinc-50 transition-colors focus:outline-none focus:ring-1 focus:ring-zinc-900">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button onClick={handleExport} disabled={exporting} className="inline-flex items-center justify-center gap-2 rounded-md border border-zinc-200 bg-white px-4 py-2 text-[13px] font-bold text-zinc-700 shadow-sm hover:bg-zinc-50 transition-colors focus:outline-none focus:ring-1 focus:ring-zinc-900">
              <Download className="h-4 w-4" />
              {exporting ? "Mengunduh..." : "Export Dashboard"}
            </button>
            <span className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider ring-1 ring-inset ${
                event.status === "closed" ? "bg-zinc-50 text-zinc-700 ring-zinc-500/20" : 
                event.status === "active" ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20" : 
                "bg-amber-50 text-amber-700 ring-amber-600/20"
              }`}>
              {event.status === "closed" ? "Event Selesai" : event.status === "active" ? "Sedang Aktif" : "Status Draft"}
            </span>
          </div>
        </div>
      </header>

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <KpiCard
          title="Total Revenue Event"
          value={fmtRp(summary.total_revenue)}
          icon={TrendingUp}
        />
        <KpiCard
          title="Total Transaksi"
          value={summary.total_transactions}
          icon={ShoppingBag}
        />
        <KpiCard
          title="Total Sesi Kasir"
          value={summary.total_sessions}
          icon={Clock}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        {/* Sessions */}
        <div className="flex flex-col overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm">
          <div className="border-b border-zinc-100 bg-zinc-50/50 px-5 py-4 shrink-0">
            <h2 className="text-[15px] font-bold text-zinc-900">Rekap Per Sesi (Harian)</h2>
            <p className="mt-0.5 text-[12px] font-medium text-zinc-500">Satu sesi mewakili satu shift operasional kasir</p>
          </div>
          {sessions.length === 0 ? (
            <div className="p-12 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-zinc-50 border border-zinc-200 mb-3">
                <Clock className="h-6 w-6 text-zinc-400" />
              </div>
              <h3 className="text-[14px] font-semibold text-zinc-900">Belum ada sesi kasir</h3>
              <p className="mt-1 text-[13px] text-zinc-500">Belum ada aktivitas kasir yang tercatat untuk event ini.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-[13px]">
                <thead className="bg-zinc-50 border-b border-zinc-200 text-zinc-600">
                  <tr>
                    <th className="px-5 py-3 font-medium w-12 text-center">#</th>
                    <th className="px-5 py-3 font-medium">Waktu Operasional</th>
                    <th className="px-5 py-3 font-medium text-right">Trx</th>
                    <th className="px-5 py-3 font-medium text-right">Revenue</th>
                    <th className="px-5 py-3 font-medium text-center w-24">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 bg-white">
                  {sessions.map((s, i) => (
                    <tr key={s.id} className="hover:bg-zinc-50/50 transition-colors">
                      <td className="px-5 py-3 text-center">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-zinc-100 text-[11px] font-bold text-zinc-600">
                          {i + 1}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="font-bold text-zinc-900">{formatDateTime(s.opened_at)}</div>
                        {s.closed_at ? (
                          <div className="text-[11px] font-medium text-zinc-400 mt-0.5">Tutup: {formatDateTime(s.closed_at)}</div>
                        ) : (
                          <div className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest mt-0.5">Sedang Aktif</div>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right font-medium tabular-nums text-zinc-600">
                        {s.transaction_count}
                      </td>
                      <td className="px-5 py-3 text-right font-bold tabular-nums text-zinc-900">
                        {fmtRpShort(s.total_sales)}
                      </td>
                      <td className="px-5 py-3 text-center">
                        <Link
                          to={`/rekap/${s.id}`}
                          className="inline-flex items-center gap-1 rounded-md border border-zinc-200 bg-white px-2.5 py-1.5 text-[11px] font-bold text-zinc-700 hover:bg-zinc-50 shadow-sm transition-colors"
                        >
                          Detail <ChevronRight className="h-3 w-3" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center justify-between border-t border-zinc-200 bg-zinc-900 px-6 py-4 text-white">
                <span className="text-[14px] font-semibold text-zinc-300">Total Akumulasi Revenue</span>
                <span className="text-xl font-bold tabular-nums tracking-tight">
                  {fmtRp(summary.total_revenue)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Right column: Top products & Stock status */}
        <div className="space-y-6">
          
          <div className="flex flex-col overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm">
            <div className="border-b border-zinc-100 bg-zinc-50/50 px-5 py-4">
              <div className="flex items-center gap-2">
                <Trophy className="h-4 w-4 text-zinc-900" />
                <h2 className="text-[15px] font-bold text-zinc-900">Produk Terlaris Global</h2>
              </div>
              <p className="mt-0.5 text-[12px] font-medium text-zinc-500">Akumulasi seluruh sesi event</p>
            </div>
            
            {top_products.length === 0 ? (
              <div className="px-5 py-10 text-center text-[13px] font-medium text-zinc-400">Belum ada data penjualan</div>
            ) : (
              <div className="divide-y divide-zinc-100 p-2">
                {top_products.slice(0, 8).map((p, i) => {
                  const sold = Number(p.total_qty_sold) || 0;
                  const max  = Number(top_products[0]?.total_qty_sold) || 1;
                  const pct  = (sold / max) * 100;
                  return (
                    <div key={p.code || i} className="px-4 py-3 rounded-md hover:bg-zinc-50 transition-colors">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-zinc-200 bg-white text-[11px] font-bold text-zinc-700 shadow-sm">
                            {i + 1}
                          </div>
                          <div className="min-w-0">
                            <div className="truncate text-[13px] font-bold text-zinc-900 leading-tight">{p.product_name}</div>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mt-0.5">{p.code}</div>
                          </div>
                        </div>
                        <div className="shrink-0 text-right">
                          <div className="text-[13px] font-bold tabular-nums text-zinc-900">{sold} pcs</div>
                          <div className="text-[11px] font-medium text-emerald-600 tabular-nums">{fmtRpShort(p.total_revenue)}</div>
                        </div>
                      </div>
                      <div className="mt-2.5 h-1.5 w-full rounded-full bg-zinc-100 overflow-hidden">
                        <div className="h-1.5 rounded-full bg-zinc-900" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Stock status indicator */}
          {top_products.length > 0 && (
            <div className="flex flex-col overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm">
              <div className="border-b border-zinc-100 bg-zinc-50/50 px-5 py-4">
                <div className="flex items-center gap-2">
                  <PackageCheck className="h-4 w-4 text-zinc-900" />
                  <h2 className="text-[15px] font-bold text-zinc-900">Indikator Laju Stok</h2>
                </div>
                <p className="mt-0.5 text-[12px] font-medium text-zinc-500">Rasio terjual terhadap stok dialokasikan</p>
              </div>
              <div className="divide-y divide-zinc-100 p-2">
                {top_products.slice(0, 6).map((p, i) => {
                  const transferred = Number(p.qty_transferred) || 0;
                  const remaining   = Number(p.qty_remaining) || 0;
                  const sold        = transferred - remaining;
                  const pct         = transferred > 0 ? (sold / transferred) * 100 : 0;
                  return (
                    <div key={p.code || i} className="px-4 py-3 rounded-md hover:bg-zinc-50 transition-colors">
                      <div className="flex items-center justify-between text-[13px]">
                        <span className="truncate font-bold text-zinc-900">{p.product_name}</span>
                        <span className="shrink-0 text-[11px] font-medium tabular-nums text-zinc-500 bg-white border border-zinc-200 rounded px-1.5 shadow-sm">
                          {sold} / {transferred}
                        </span>
                      </div>
                      <div className="mt-2.5 h-1.5 w-full rounded-full bg-zinc-100 overflow-hidden">
                        <div
                          className={`h-1.5 rounded-full ${pct >= 90 ? "bg-rose-500" : pct >= 50 ? "bg-amber-500" : "bg-emerald-500"}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ title, value, icon: Icon }) {
  return (
    <div className="flex flex-col justify-center rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-zinc-50 border border-zinc-100">
          <Icon className="h-4 w-4 text-zinc-600" />
        </div>
        <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-500">{title}</div>
      </div>
      <div className="text-2xl font-bold tracking-tight text-zinc-900 tabular-nums">
        {value}
      </div>
    </div>
  );
}

function fmtRp(n) {
  return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(Number(n) || 0);
}

function fmtRpShort(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}jt`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(0)}rb`;
  return fmtRp(v);
}

export default RekapEvent;